import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsRepository } from '@/services';
import { supabase, debouncedInvalidate } from '@/services/supabaseClient';
import { useTenant } from './useTenant';
import { toast } from 'sonner';

export interface ProductVariationAttribute {
  id: string;
  product_id: string;
  attribute_type: string;
  attribute_value: string;
  position: number;
  value_position: number;
  created_at: string;
}

export interface ProductVariation {
  id: string;
  product_id: string;
  stock: number;
  created_at: string;
  updated_at: string;
  attribute_links?: Array<{ attribute_id: string }>;
  attributes?: ProductVariationAttribute[];
}

export interface Product {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  category_id: string | null;
  image_urls: string[] | null;
  cost_price: number | null;
  sale_price: number | null;
  price_pix: number | null;
  product_code: string | null;
  has_variations: boolean;
  stock: number | null;
  weight: number | null;
  height: number | null;
  width: number | null;
  depth: number | null;
  created_at: string;
  updated_at: string;
  category?: {
    id: string;
    name: string;
  };
  variation_attributes?: ProductVariationAttribute[];
  variations?: ProductVariation[];
}

export interface VariationAttributeGroup {
  name: string;
  values: string[];
}

export interface ProductFormData {
  name: string;
  description?: string;
  category_id?: string;
  image_urls?: string[];
  cost_price?: number;
  sale_price?: number;
  price_pix?: number;
  product_code?: string;
  has_variations: boolean;
  stock?: number;
  weight?: number;
  height?: number;
  width?: number;
  depth?: number;
  attributeGroups?: VariationAttributeGroup[];
  variationStocks?: Record<string, number>;
}

export function resolveVariationAttributes(product: Product): Product {
  if (!product.variations || !product.variation_attributes) return product;

  const attrMap = new Map(product.variation_attributes.map(a => [a.id, a]));

  const resolvedVariations = product.variations.map(v => ({
    ...v,
    attributes: (v.attribute_links || [])
      .map(link => attrMap.get(link.attribute_id))
      .filter((a): a is ProductVariationAttribute => !!a)
      .sort((a, b) => a.position - b.position),
  }));

  return { ...product, variations: resolvedVariations };
}


export function useProducts(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { tenant, loading: tenantLoading } = useTenant();
  const queryClient = useQueryClient();

  const productsQuery = useQuery({
    queryKey: ['products', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];

      const { data, error } = await productsRepository.list(tenant.id);

      if (error) throw error;
      return (data as Product[]).map(resolveVariationAttributes);
    },
    enabled: enabled && !!tenant?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!enabled || !tenant?.id) return;
    return productsRepository.subscribe(tenant.id, () => {
      debouncedInvalidate(queryClient, ['products', tenant.id]);
      debouncedInvalidate(queryClient, ['products-paginated']);
    });
  }, [enabled, tenant?.id, queryClient]);

  const createProduct = useMutation({
    mutationFn: async (formData: ProductFormData) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');

      const activeGroups = formData.has_variations
        ? (formData.attributeGroups || []).filter(g => g.name && g.values.length > 0)
        : [];

      const { data, error } = await supabase.rpc('create_product_with_variations', {
        p_tenant_id: tenant.id,
        p_product_data: {
          name: formData.name,
          description: formData.description || null,
          category_id: formData.category_id || null,
          image_urls: formData.image_urls || [],
          cost_price: formData.cost_price ?? null,
          sale_price: formData.sale_price ?? null,
          price_pix: formData.price_pix ?? null,
          product_code: formData.product_code?.toUpperCase() || null,
          has_variations: formData.has_variations,
          stock: formData.has_variations ? null : (formData.stock ?? 0),
          weight: formData.weight ?? null,
          height: formData.height ?? null,
          width: formData.width ?? null,
          depth: formData.depth ?? null,
        },
        p_attribute_groups: activeGroups.length > 0 ? activeGroups : null,
        p_variation_stocks: formData.has_variations ? (formData.variationStocks || {}) : {},
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ['products-paginated'] });
      toast.success('Produto criado com sucesso!');
    },
    onError: (error: Error) => {
      if (error.message?.includes('unique_product_code_per_tenant') || error.message?.includes('duplicate') || error.message?.includes('product_code')) {
        toast.error('Este código de produto já está em uso. Escolha outro código.');
      } else if (error.message?.includes('Tenant') || error.message?.includes('tenant')) {
        toast.error('Sua conta não está vinculada a uma empresa. Saia e entre novamente.');
      } else if (error.message) {
        toast.error(`Erro ao criar produto: ${error.message}`);
      } else {
        toast.error('Erro ao criar produto. Tente novamente.');
      }
    },
  });

  const updateProduct = useMutation({
    mutationFn: async ({ id, formData, expectedUpdatedAt }: { id: string; formData: ProductFormData; expectedUpdatedAt?: string }) => {
      const activeGroups = formData.has_variations
        ? (formData.attributeGroups || []).filter(g => g.name && g.values.length > 0)
        : [];

      const { data, error } = await supabase.rpc('update_product_with_variations', {
        p_tenant_id: tenant!.id,
        p_product_id: id,
        p_product_data: {
          name: formData.name,
          description: formData.description || null,
          category_id: formData.category_id || null,
          image_urls: formData.image_urls || [],
          cost_price: formData.cost_price ?? null,
          sale_price: formData.sale_price ?? null,
          price_pix: formData.price_pix ?? null,
          product_code: formData.product_code?.toUpperCase() || null,
          has_variations: formData.has_variations,
          stock: formData.has_variations ? null : (formData.stock ?? 0),
          weight: formData.weight ?? null,
          height: formData.height ?? null,
          width: formData.width ?? null,
          depth: formData.depth ?? null,
        },
        p_attribute_groups: activeGroups.length > 0 ? activeGroups : null,
        p_variation_stocks: formData.has_variations ? (formData.variationStocks || {}) : {},
        p_expected_updated_at: expectedUpdatedAt || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ['products-paginated'] });
      toast.success('Produto atualizado!');
    },
    onError: (error: Error) => {
      if (error.message?.includes('carrinhos, reservas ou vendas')) {
        toast.error('Existem carrinhos, reservas ou vendas ativas com essas variações. Cancele-as antes de remover.');
      } else if (error.message?.includes('CONFLICT:')) {
        toast.error('Este produto foi alterado por outro usuário. Feche e reabra o formulário.');
      } else if (error.message?.includes('unique_product_code_per_tenant') || error.message?.includes('duplicate') || error.message?.includes('product_code')) {
        toast.error('Este código de produto já está em uso. Escolha outro código.');
      } else if (error.message) {
        toast.error(`Erro ao atualizar produto: ${error.message}`);
      } else {
        toast.error('Erro ao atualizar produto. Tente novamente.');
      }
    },
  });

  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await productsRepository.archive(tenant!.id, id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ['products-paginated'] });
      toast.success('Produto removido!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao remover produto');
    },
  });

  const getTotalStock = (product: Product): number => {
    if (!product.has_variations) {
      return product.stock || 0;
    }

    return product.variations?.reduce((total, v) => total + v.stock, 0) || 0;
  };

  return {
    products: productsQuery.data ?? [],
    isLoading: productsQuery.isLoading || tenantLoading,
    error: productsQuery.error,
    createProduct,
    updateProduct,
    deleteProduct,
    getTotalStock,
  };
}

